/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * ConnectionStatusBanner.tsx — Connectio\1 \2tatu\1 \2anner (services/operator-web-app).
 */

import { useEffect, useRef } from 'react';
import { useOperatorStore } from '@/store';
import { useNow } from '@/hooks/useNow';
import { Button } from '@/components/ui';
import { appConfig } from '@/config/env';
import { getMetricsCollector } from '@/observability/metrics';
import { useTranslation } from '@/i18n';

const DEGRADED_THRESHOLD_FRACTION = 0.6; // warn when no server activity for 60% of TTL
const NOW_POLL_MS = 2000;

export function ConnectionStatusBanner() {
  const t = useTranslation();
  const connectionState = useOperatorStore((s) => s.connectionState);
  const presence = useOperatorStore((s) => s.presence);
  const lastServerActivityAt = useOperatorStore((s) => s.lastServerActivityAt);
  const connect = useOperatorStore((s) => s.connect);
  const now = useNow(NOW_POLL_MS);
  const wasDegradedRef = useRef(false);

  const isOffline =
    presence.status === 'offline' ||
    (connectionState === 'disconnected' && lastServerActivityAt !== null);
  const isDegraded =
    connectionState === 'connected' &&
    lastServerActivityAt != null &&
    presence.presenceTtlMs > 0 &&
    now - lastServerActivityAt > presence.presenceTtlMs * DEGRADED_THRESHOLD_FRACTION;

  useEffect(() => {
    if (isDegraded && !wasDegradedRef.current) {
      wasDegradedRef.current = true;
      getMetricsCollector().reportPresenceTimeoutIncident();
    }
    if (!isDegraded) wasDegradedRef.current = false;
  }, [isDegraded]);

  if (connectionState === 'connecting' || connectionState === 'reconnecting') {
    return (
      <div
        className="flex items-center justify-center gap-2 px-4 py-2 bg-sky-50 dark:bg-sky-900/20 border-b border-sky-200 dark:border-sky-800 text-sky-800 dark:text-sky-200 text-sm"
        role="status"
        aria-live="polite"
      >
        <span className="animate-pulse">●</span>
        {connectionState === 'reconnecting'
          ? t('connection.reconnecting')
          : t('connection.connecting')}
      </div>
    );
  }

  if (isDegraded) {
    return (
      <div
        className="flex items-center justify-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm"
        role="alert"
        aria-label={t('connection.unstable_label')}
      >
        <span>⚠</span>
        {t('connection.unstable')}
      </div>
    );
  }

  if (isOffline) {
    return (
      <div
        className="flex items-center justify-center gap-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-sm"
        role="alert"
        aria-label={t('connection.offline_label')}
      >
        <span>{t('connection.offline')}</span>
        <Button
          variant="primary"
          size="sm"
          onClick={() => connect(appConfig.wsOperatorUrl)}
          aria-label={t('connection.reconnect')}
        >
          {t('connection.reconnect')}
        </Button>
      </div>
    );
  }

  return null;
}
